import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { C } from "../ipd-billing/constants";
import { PageHeader } from "../rule-builder/ui";
import {
  formatDateTime,
  labReportObservationsForOrder,
  normalizeLabResultRows,
  flattenObservationPanels,
  observationCommentText,
  readLabReport,
  computeAge,
} from "../lab-technician/labOrdersShared";
import {
  generateLabReportPdf,
  formatLabReportBed,
  resolveReferringDoctor,
  withDoctorPrefix,
} from "../lab-technician/utils/printLabReport";
import { formatAgeGender } from "../../utils/reportSheet";
import { fetchReport } from "../radiology-technician/api/radiologyReportApi";
import { generateRadiologyReportPdf } from "../radiology-technician/utils/printRadiologyReport";
import { BrandingContext } from "../../BrandingProvider";
import { usePermissions } from "../login/PermissionContext";
import DiagnosticReportPdfModal from "./DiagnosticReportPdfModal";
import IpdPatientConditionDialog from "./IpdPatientConditionDialog";
import EmergencyPatientConditionDialog from "./EmergencyPatientConditionDialog";
import PatientTimelineDialog from "./PatientTimelineDialog";
import { invalidAccessLogout } from "../../components/Utils/InvalidAccessLogout";
import {
  getMyDiagnosticAssignments,
  getDiagnosticReportAssignments,
  invalidateDiagnosticReportAssignments,
  signDiagnosticReport,
  rejectDiagnosticReport,
  publishDiagnosticReport,
  rememberQueueDoctorIdentity,
  isMyDiagnosticAssignment,
  signedDiagnosticSignatoriesFromBundle,
  extractDiagnosticSignatureImage,
  markLabOrderVerified,
  getDiagnosticDoctorRoleType,
  getDiagnosticDoctorCopy,
  classifyDiagnosticError,
  ASSIGNMENT_STATUS_META,
  PUBLISH_STATUS_META,
  publishRuleHint,
  QUEUE_TABS,
} from "./diagnosticReportApi";

function ConfirmCard({ title, children, actions }) {
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: 420,
        maxWidth: "92%",
        background: "#fff",
        borderRadius: 12,
        padding: "20px 22px 16px",
        boxShadow: "0 16px 40px rgba(0,0,0,0.25)",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 18, color: C.text, mb: 1 }}>
        {title}
      </Typography>
      {children}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginTop: 16,
        }}
      >
        {actions}
      </div>
    </div>
  );
}

function StatusChip({ meta }) {
  if (!meta) return null;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        color: meta.color,
        background: meta.bg,
      }}
    >
      {meta.label}
    </span>
  );
}

function displayWhen(value) {
  if (!value) return "—";
  const formatted = formatDateTime(value);
  return formatted && formatted !== "-" ? formatted : "—";
}

function QueueTabs({ value, onChange, pendingCount }) {
  return (
    <div
      style={{
        display: "inline-flex",
        gap: 4,
        background: "#eef1f4",
        borderRadius: 10,
        padding: 4,
        marginBottom: 20,
      }}
    >
      {QUEUE_TABS.map((t) => {
        const active = value === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 700,
              background: active ? "#fff" : "transparent",
              color: active ? C.text : C.textSub,
              boxShadow: active ? "0 1px 3px rgba(13,35,63,0.12)" : "none",
            }}
          >
            {t.label}
            {t.id === "assigned" && pendingCount > 0 && (
              <span
                style={{
                  minWidth: 18,
                  padding: "1px 6px",
                  borderRadius: 999,
                  fontSize: 11,
                  background: active ? C.primary : "#d7dde5",
                  color: active ? "#fff" : C.text,
                }}
              >
                {pendingCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function emptyCopy(copy, tab) {
  if (tab === "assigned") return copy.emptyPending;
  if (tab === "signed") return "No signed reports";
  if (tab === "rejected") return "No rejected reports";
  return copy.emptyGeneric;
}

function tabSubtitle(copy, tab) {
  const label = QUEUE_TABS.find((t) => t.id === tab)?.label || "Pending";
  return `${copy.domain} — ${label} Reports`;
}

const pickPatientField = (...values) => {
  for (const value of values) {
    const s = value == null ? "" : String(value).trim();
    if (s && s !== "-") return s;
  }
  return "";
};

function fallbackLabOrder(report, patientLabTestId) {
  const orderNo = report?.order_no || "";
  return {
    orderNo,
    service: report?.test || "—",
    status: report?.status || "Reported",
    results: normalizeLabResultRows(report?.results) || [],
    ordered: report?.ordered_time || "",
    priority: report?.priority || "",
    patientLabTestId,
    doctor: report?.doctor_name || "",
    department: pickPatientField(report?.department, report?.classification_name),
  };
}

function enrichLabPatientFromReport(patient, report, order) {
  const rp = report?.patient || {};
  const adm = report?.admission || {};
  const dob = pickPatientField(
    patient?.dob,
    rp.dob,
    report?.dob,
    report?.patient_dob,
    adm.dob,
  );
  const ageRaw =
    patient?.age ??
    rp.age ??
    report?.age ??
    report?.patient_age ??
    report?.age_years ??
    adm.age ??
    null;
  const resolvedAge =
    ageRaw != null && ageRaw !== "" && ageRaw !== "-"
      ? ageRaw
      : computeAge(dob);
  return {
    ...patient,
    name: pickPatientField(
      patient?.name,
      rp.name,
      report?.patient_name,
      report?.patient?.name,
    ),
    uhid: pickPatientField(patient?.uhid, rp.uhid, report?.uhid),
    patientId: pickPatientField(
      patient?.patientId,
      rp.patient_id,
      report?.patient_id,
    ),
    admissionId: pickPatientField(
      patient?.admissionId,
      report?.admission_id,
      adm.admission_id,
      adm.id,
    ),
    patientType: pickPatientField(patient?.patientType, report?.patient_type),
    bedNo: pickPatientField(
      patient?.bedNo,
      rp.bed_no,
      rp.bedNo,
      report?.bed_no,
      report?.bedNo,
      report?.bed_code,
      adm.bed_no,
      adm.bed_code,
    ),
    roomNo: pickPatientField(
      patient?.roomNo,
      rp.room_no,
      rp.roomNo,
      rp.bed_type,
      rp.bed_type_name,
      rp.ward_name,
      report?.room_no,
      report?.roomNo,
      report?.ward_name,
      adm.room_no,
      adm.ward_name,
    ),
    ward: pickPatientField(
      patient?.ward,
      rp.ward_name,
      rp.ward,
      report?.ward_name,
      report?.ward,
      adm.ward_name,
      adm.ward,
    ),
    dob,
    age: resolvedAge,
    gender: pickPatientField(
      patient?.gender,
      rp.gender,
      report?.gender,
      report?.patient_gender,
      adm.gender,
    ),
    doctorName: pickPatientField(
      patient?.doctorName,
      rp.doctor_name,
      report?.doctor_name,
      order?.doctor,
      report?.referring_doctor_name,
      report?.referring_doctor,
    ),
  };
}

function fallbackLabPatient(report, order) {
  return enrichLabPatientFromReport(
    {
      name: report?.patient?.name || "",
      uhid: report?.patient?.uhid || "",
      patientId: report?.patient?.patient_id || report?.patient_id || "",
      admissionId: report?.admission_id || "",
      patientType: report?.patient_type || "",
      orders: [order],
    },
    report,
    order,
  );
}

function finalizeLabReviewPayload(
  fullReport,
  patient,
  order,
  observations,
  extraOrders = [],
) {
  const mergedOrder = {
    ...order,
    doctor: pickPatientField(
      order?.doctor,
      fullReport?.patient?.doctor_name,
      fullReport?.doctor_name,
      fullReport?.referring_doctor_name,
    ),
    department: pickPatientField(
      order?.department,
      fullReport?.department,
      fullReport?.classification_name,
      fullReport?.service_classification_name,
    ),
  };
  return {
    patient: enrichLabPatientFromReport(
      { ...patient, orders: [mergedOrder, ...extraOrders] },
      fullReport,
      mergedOrder,
    ),
    observations,
  };
}

// Tests drawn on the same sample are reviewed as one report. The queue lists
// one row per test, so same-sample rows are found by what they share: the
// patient, the admission and the moment the tests were ordered (or the batch
// id when the backend sends one).
const rowBatchId = (row) => {
  const report = row?.report || {};
  return String(
    report.lab_test_batch_id || report.batch_id || row?.lab_test_batch_id || "",
  );
};

const rowPatientKey = (row) => {
  const report = row?.report || {};
  const patient = report.patient || {};
  const who = patient.uhid || patient.patient_id || report.patient_id || "";
  return who ? `${who}|${report.admission_id || ""}` : "";
};

const rowOrderedAt = (row) =>
  String(row?.report?.ordered_time || row?.ordered_time || "");

// Submit for Review assigns every test on the sample in one go, so their
// assigned_at stamps land within seconds of each other.
const SAME_SAMPLE_ASSIGN_WINDOW_MS = 15000;

const isSameLabSample = (a, b) => {
  if (!a || !b || a === b) return false;
  const batchA = rowBatchId(a);
  const batchB = rowBatchId(b);
  if (batchA || batchB) return batchA === batchB;
  const patientKey = rowPatientKey(a);
  if (!patientKey || patientKey !== rowPatientKey(b)) return false;
  const orderedA = rowOrderedAt(a);
  if (orderedA && orderedA === rowOrderedAt(b)) return true;
  const assignedA = Date.parse(a.assigned_at);
  const assignedB = Date.parse(b.assigned_at);
  return (
    Number.isFinite(assignedA) &&
    Number.isFinite(assignedB) &&
    Math.abs(assignedA - assignedB) <= SAME_SAMPLE_ASSIGN_WINDOW_MS
  );
};

const rowPatientLabTestId = (row) => {
  const id = Number(row?.report?.patient_lab_test_id || row?.patient_lab_test_id);
  return Number.isFinite(id) && id > 0 ? id : null;
};

function loadLabReviewPayload(report, patientLabTestId, bundle, siblingRows = []) {
  const fullReport = {
    ...(bundle?.report || {}),
    ...(report || {}),
  };
  const orderNo = String(fullReport?.order_no || "").trim();
  const seedOrder = fallbackLabOrder(fullReport, patientLabTestId);
  const observations = labReportObservationsForOrder(orderNo, seedOrder);
  const patient = fallbackLabPatient(fullReport, seedOrder);

  const extraOrders = [];
  let allObservations = observations;
  siblingRows.forEach((row) => {
    const siblingReport = row?.report || {};
    const siblingOrder = fallbackLabOrder(
      siblingReport,
      rowPatientLabTestId(row),
    );
    extraOrders.push(siblingOrder);
    allObservations = {
      ...allObservations,
      ...labReportObservationsForOrder(
        String(siblingReport.order_no || "").trim(),
        siblingOrder,
      ),
    };
  });

  return finalizeLabReviewPayload(
    fullReport,
    patient,
    seedOrder,
    allObservations,
    extraOrders,
  );
}

function fallbackRadOrder(report, patientLabTestId) {
  const orderNo = report?.order_no || "";
  return {
    orderNo,
    service: report?.test || "—",
    status: report?.status || "Reported",
    modality: pickPatientField(report?.modality, report?.classification_name),
    priority: report?.priority || "",
    ordered: report?.ordered_time || "",
    scheduled: report?.scheduled_time || report?.scheduled || "",
    doctor: pickPatientField(report?.doctor_name, report?.patient?.doctor_name),
    medicalServiceId: report?.medical_service_id || "",
    department: pickPatientField(
      report?.department,
      report?.classification_name,
      report?.service_classification_name,
    ),
    patientLabTestId,
  };
}

function finalizeRadReviewPayload(fullReport, patient, order, radReport) {
  const mergedOrder = {
    ...order,
    doctor: pickPatientField(
      order?.doctor,
      fullReport?.patient?.doctor_name,
      fullReport?.doctor_name,
      fullReport?.referring_doctor_name,
    ),
    department: pickPatientField(
      order?.department,
      fullReport?.department,
      fullReport?.classification_name,
      fullReport?.service_classification_name,
    ),
    modality: pickPatientField(
      order?.modality,
      fullReport?.modality,
      fullReport?.classification_name,
    ),
  };
  return {
    patient: enrichLabPatientFromReport(
      { ...patient, orders: [mergedOrder] },
      fullReport,
      mergedOrder,
    ),
    order: mergedOrder,
    radReport,
  };
}

async function loadRadReviewPayload(report, bundle) {
  const fullReport = {
    ...(bundle?.report || {}),
    ...(report || {}),
  };
  const orderNo = String(fullReport?.order_no || "").trim();
  const pltId = Number(fullReport?.patient_lab_test_id);
  const seedOrder = fallbackRadOrder(
    fullReport,
    Number.isFinite(pltId) && pltId > 0 ? pltId : null,
  );
  const patient = fallbackLabPatient(fullReport, seedOrder);
  let radReport = null;

  if (orderNo) {
    try {
      radReport = await fetchReport(orderNo);
    } catch (err) {
      console.warn("fetchReport:", err);
    }
  }

  return finalizeRadReviewPayload(fullReport, patient, seedOrder, radReport);
}

function resolveLabPdfRows(order, observations) {
  const livePanels = observations?.[order.orderNo];
  const liveRows = flattenObservationPanels(livePanels);
  const liveComment = observationCommentText(livePanels);
  if (liveRows.length > 0) {
    return { rows: liveRows, interpretation: liveComment || null };
  }
  const stored = readLabReport(order.orderNo);
  if (stored) {
    return {
      rows: stored.rows || [],
      interpretation:
        observationCommentText(stored.panels) || liveComment || null,
    };
  }
  if (order.results && order.results.length > 0) {
    return {
      rows: order.results,
      interpretation: order.interpretation || liveComment || null,
    };
  }
  return { rows: [], interpretation: liveComment || null };
}

function ReviewActions({
  copy,
  bundle,
  queuePk,
  acting,
  published,
  showSign,
  showReject,
  showPublish,
  onSign,
  onReject,
  onPublish,
}) {
  const myAssignment = (bundle?.assignments || []).find((a) =>
    isMyDiagnosticAssignment(a, queuePk)
  );
  return (
    <>
      {bundle?.publish_rule ? (
        <Typography variant="caption" sx={{ color: C.textSub, mr: 1 }}>
          {publishRuleHint(bundle.publish_rule)}
        </Typography>
      ) : null}
      {myAssignment ? (
        <StatusChip meta={ASSIGNMENT_STATUS_META[myAssignment.status]} />
      ) : null}
      {published ? <StatusChip meta={PUBLISH_STATUS_META.published} /> : null}
      {showSign && (
        <Button
          type="button"
          variant="contained"
          size="small"
          disabled={acting}
          onClick={onSign}
        >
          {copy.sign}
        </Button>
      )}
      {showReject && (
        <Button
          type="button"
          color="error"
          variant="outlined"
          size="small"
          disabled={acting}
          onClick={onReject}
        >
          Reject
        </Button>
      )}
      {showPublish && (
        <Button
          type="button"
          color="success"
          variant="contained"
          size="small"
          disabled={acting}
          onClick={onPublish}
        >
          {copy.publish}
        </Button>
      )}
    </>
  );
}

function ReviewReportOverlay({ item, siblings = [], copy, onBack, onUpdated }) {
  const isRadiologist = copy.role === "radiologist";
  const brandingData = React.useContext(BrandingContext);
  const { userInfo } = usePermissions();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [publishOpen, setPublishOpen] = useState(false);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [labView, setLabView] = useState(null);
  const [radView, setRadView] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [conditionOpen, setConditionOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const pdfUrlRef = useRef("");

  const itemRef = useRef(item);
  itemRef.current = item;
  const reviewCancelledRef = useRef(false);
  const reviewInflightRef = useRef(null);

  const patientLabTestId = Number(
    item?.report?.patient_lab_test_id || item?.patient_lab_test_id
  );
  const queuePk = item?.diagnostic_doctor_id;

  const loadReview = useCallback(async () => {
    if (!Number.isFinite(patientLabTestId) || patientLabTestId <= 0) {
      toast.error("Missing patient_lab_test_id for this report");
      onBack();
      return;
    }
    if (reviewInflightRef.current) {
      try {
        await reviewInflightRef.current;
      } catch {
        // A parallel load failed — the caller below still owns the UI state.
      }
      return;
    }
    setLoading(true);
    const run = (async () => {
    try {
      const res = await getDiagnosticReportAssignments(patientLabTestId);
      if (reviewCancelledRef.current) return;
      const data = res.data || null;
      setBundle(data);
      const queueItem = itemRef.current;
      const report = { ...(queueItem?.report || {}), ...(data?.report || {}) };
      if (isRadiologist) {
        setRadView(await loadRadReviewPayload(report, data));
      } else {
        setLabView(
          loadLabReviewPayload(report, patientLabTestId, data, siblings),
        );
      }
    } catch (err) {
      if (reviewCancelledRef.current) return;
      const kind = classifyDiagnosticError(err);
      const msg = err?.message || "Could not load report";
      toast.error(msg);
      if (kind === "logout") {
        invalidAccessLogout({ skipLogoutApi: true });
        return;
      }
      if (kind === "not_assigned") {
        onBack();
        return;
      }
      const queueItem = itemRef.current;
      const report = queueItem?.report || {};
      if (isRadiologist) {
        setRadView(await loadRadReviewPayload(report, null));
      } else {
        setLabView(
          loadLabReviewPayload(report, patientLabTestId, null, siblings),
        );
      }
    } finally {
      if (!reviewCancelledRef.current) setLoading(false);
    }
    })();
    reviewInflightRef.current = run;
    try {
      await run;
    } finally {
      reviewInflightRef.current = null;
    }
  }, [patientLabTestId, isRadiologist, onBack, siblings]);

  useEffect(() => {
    reviewCancelledRef.current = false;
    loadReview();
    return () => {
      reviewCancelledRef.current = true;
    };
  }, [loadReview]);

  useEffect(() => {
    let cancelled = false;
    const revoke = () => {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = "";
      }
    };

    const makePdf = async () => {
      if (loading) return;
      setPdfLoading(true);
      revoke();
      setPdfUrl("");
      try {
        let blob = null;
        if (isRadiologist && radView) {
          blob = await generateRadiologyReportPdf({
            patient: radView.patient,
            order: radView.order,
            report: radView.radReport,
            brandingData,
            userInfo,
            autoPrint: false,
            patientLabTestId,
            signatories: signedDiagnosticSignatoriesFromBundle(bundle),
          });
        } else if (!isRadiologist && labView?.patient) {
          const reportMeta = {
            ...(item?.report || {}),
            ...(bundle?.report || {}),
          };
          const enrichedPatient = enrichLabPatientFromReport(
            labView.patient,
            reportMeta,
            labView.patient.orders?.[0],
          );
          const orders = (enrichedPatient.orders || []).map((o) => {
            const { rows, interpretation } = resolveLabPdfRows(
              o,
              labView.observations
            );
            return {
              orderNo: o.orderNo,
              service: o.service,
              cpt: o.cpt,
              ordered: o.ordered || reportMeta?.ordered_time || "",
              doctor: pickPatientField(
                o.doctor,
                enrichedPatient.doctorName,
                reportMeta?.doctor_name,
              ),
              department: pickPatientField(
                o.department,
                reportMeta?.department,
                reportMeta?.classification_name,
              ),
              technician: pickPatientField(
                o.technician,
                reportMeta?.technician_name,
                reportMeta?.created_by,
              ),
              status: o.status,
              rows,
              interpretation,
            };
          });
          const technician = withDoctorPrefix(
            orders[0]?.technician ||
              reportMeta?.technician_name ||
              reportMeta?.created_by ||
              "",
          );
          blob = await generateLabReportPdf({
            patient: {
              ...enrichedPatient,
              age:
                enrichedPatient.age ??
                computeAge(enrichedPatient.dob),
            },
            orders,
            generatedAt: new Date().toISOString(),
            technician,
            brandingData,
            userInfo,
            autoPrint: false,
            patientLabTestId,
            signatories: signedDiagnosticSignatoriesFromBundle(bundle),
          });
        }
        if (cancelled || !blob) {
          if (!cancelled) setPdfLoading(false);
          return;
        }
        const url = URL.createObjectURL(blob);
        pdfUrlRef.current = url;
        setPdfUrl(url);
      } catch (err) {
        console.error("Could not build review PDF:", err);
        if (!cancelled) toast.error("Could not generate the report PDF");
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    };

    makePdf();
    return () => {
      cancelled = true;
      revoke();
    };
  }, [loading, isRadiologist, labView, radView, brandingData, userInfo, bundle, patientLabTestId]);

  const myAssignment = (bundle?.assignments || []).find((a) =>
    isMyDiagnosticAssignment(a, queuePk)
  );
  const report = bundle?.report || item?.report || {};
  const published = report.report_publish_status === "published";
  const patient = report.patient || {};
  const mergedReport = { ...(item?.report || {}), ...(bundle?.report || {}), ...report };
  const reviewPatient =
    (isRadiologist ? radView?.patient : labView?.patient) ||
    enrichLabPatientFromReport(
      patient,
      mergedReport,
      labView?.patient?.orders?.[0],
    );
  const reviewOrder =
    (isRadiologist ? radView?.order : labView?.patient?.orders?.[0]) || null;
  const pdfDetails = [
    { label: "Patient", value: reviewPatient.name || patient.name },
    { label: "UHID", value: reviewPatient.uhid || patient.uhid },
    {
      label: "Age / Gender",
      value: formatAgeGender(reviewPatient.age, reviewPatient.gender),
    },
    { label: "Bed", value: formatLabReportBed(reviewPatient) },
    {
      label: "Ref. Doctor",
      value:
        resolveReferringDoctor(reviewOrder, reviewPatient) ||
        withDoctorPrefix(reviewPatient.doctorName) ||
        "—",
    },
    {
      label: "Department",
      value:
        pickPatientField(
          reviewOrder?.department,
          mergedReport?.department,
          mergedReport?.classification_name,
          mergedReport?.service_classification_name,
        ) || "—",
    },
    { label: "Order no", value: report.order_no },
    {
      label: "Test",
      value: [report.test, ...siblings.map((r) => r.report?.test)]
        .filter(Boolean)
        .join(", "),
    },
    { label: "Priority", value: report.priority },
    { label: "Patient type", value: report.patient_type },
    { label: "Lab status", value: report.status },
    { label: "Ordered", value: displayWhen(report.ordered_time) },
  ];

  const apply = (res) => {
    invalidateDiagnosticReportAssignments(patientLabTestId);
    if (res?.data) setBundle(res.data);
    const mine = (res?.data?.assignments || []).find((a) =>
      isMyDiagnosticAssignment(a, queuePk)
    );
    const signedImage =
      extractDiagnosticSignatureImage(
        mine?.doctor_signature || mine?.signature
      ) || "";
    if (signedImage) {
      try {
        sessionStorage.setItem("diagnostic_doctor_signature", signedImage);
      } catch (err) {
        // ignore quota
      }
    }
    onUpdated?.();
    if (res?.message) toast.success(res.message);
  };

  const handleActionError = async (err) => {
    const kind = classifyDiagnosticError(err);
    const msg = err?.message || "Request failed";
    if (kind === "logout") {
      toast.error(msg);
      invalidAccessLogout({ skipLogoutApi: true });
      return;
    }
    if (kind === "signature") {
      setSignatureOpen(true);
      toast.error(msg);
      return;
    }
    if (kind === "not_assigned") {
      toast.error(msg);
      onBack();
      return;
    }
    if (kind === "stale" || kind === "readonly") {
      toast.error(msg);
      await loadReview();
      return;
    }
    toast.error(msg);
  };

  const showSign = !published && myAssignment?.status === "assigned";
  const showReject = showSign;
  const showPublish =
    !published &&
    myAssignment?.status === "signed" &&
    bundle?.can_publish === true;

  const admissionId =
    reviewPatient?.admissionId ||
    mergedReport?.admission_id ||
    report?.admission_id ||
    "";
  const patientType = String(
    reviewPatient?.patientType ||
      mergedReport?.patient_type ||
      report?.patient_type ||
      "",
  ).toUpperCase();
  const canViewIpdCondition =
    patientType === "IPD" && String(admissionId || "").trim() !== "";
  const emergencyVisitId =
    reviewPatient?.visitId ||
    mergedReport?.visit_id ||
    report?.visit_id ||
    "";
  const canViewEmergencyCondition =
    patientType === "EMERGENCY" && String(emergencyVisitId || "").trim() !== "";
  const timelinePatientId = pickPatientField(
    reviewPatient?.patientId,
    patient?.patient_id,
    patient?.id,
    mergedReport?.patient_id,
    mergedReport?.patient?.patient_id,
    mergedReport?.patient?.id,
  );
  const canViewPatientTimeline =
    patientType === "OPD" ||
    (patientType !== "IPD" &&
      patientType !== "EMERGENCY" &&
      String(admissionId || "").trim() === "");

  const requirePatientLabTestId = () => {
    if (!Number.isFinite(patientLabTestId) || patientLabTestId <= 0) {
      toast.error("Missing patient_lab_test_id for this report");
      return false;
    }
    return true;
  };

  const footerExtra = (
    <ReviewActions
      copy={copy}
      bundle={bundle}
      queuePk={queuePk}
      acting={acting}
      published={published}
      showSign={showSign}
      showReject={showReject}
      showPublish={showPublish}
      onSign={async () => {
        if (!requirePatientLabTestId()) return;
        setActing(true);
        try {
          const res = await signDiagnosticReport(patientLabTestId);
          for (const row of siblings) {
            const id = rowPatientLabTestId(row);
            if (id) await signDiagnosticReport(id);
          }
          apply(res);
        } catch (e) {
          await handleActionError(e);
        } finally {
          setActing(false);
        }
      }}
      onReject={() => setRejectOpen(true)}
      onPublish={() => setPublishOpen(true)}
    />
  );

  const overlay = rejectOpen ? (
    <ConfirmCard
      title="Reject report"
      actions={
        <>
          <Button type="button" onClick={() => setRejectOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            color="error"
            variant="contained"
            disabled={acting}
            onClick={async () => {
              if (!rejectReason.trim()) {
                toast.error("Reason is required");
                return;
              }
              if (!requirePatientLabTestId()) return;
              setActing(true);
              try {
                const res = await rejectDiagnosticReport(
                  patientLabTestId,
                  rejectReason,
                );
                for (const row of siblings) {
                  const id = rowPatientLabTestId(row);
                  if (id) await rejectDiagnosticReport(id, rejectReason);
                }
                apply(res);
                setRejectOpen(false);
                setRejectReason("");
              } catch (e) {
                await handleActionError(e);
              } finally {
                setActing(false);
              }
            }}
          >
            Confirm
          </Button>
        </>
      }
    >
      <TextField
        label="Reason"
        required
        multiline
        minRows={3}
        fullWidth
        value={rejectReason}
        onChange={(e) => setRejectReason(e.target.value)}
        sx={{ mt: 1 }}
      />
    </ConfirmCard>
  ) : publishOpen ? (
    <ConfirmCard
      title={copy.publish}
      actions={
        <>
          <Button type="button" onClick={() => setPublishOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            color="success"
            variant="contained"
            disabled={acting}
            onClick={async () => {
              if (!requirePatientLabTestId()) return;
              setActing(true);
              try {
                const res = await publishDiagnosticReport(patientLabTestId);
                for (const row of siblings) {
                  const id = rowPatientLabTestId(row);
                  if (id) await publishDiagnosticReport(id);
                }
                apply(res);
                for (const verifiedReport of [
                  report,
                  ...siblings.map((row) => row.report).filter(Boolean),
                ]) {
                  try {
                    await markLabOrderVerified(verifiedReport);
                  } catch (statusErr) {
                    console.warn(
                      "Could not set order status to Verified:",
                      statusErr,
                    );
                  }
                }
                setPublishOpen(false);
              } catch (e) {
                await handleActionError(e);
              } finally {
                setActing(false);
              }
            }}
          >
            Confirm publish
          </Button>
        </>
      }
    >
      <Typography variant="body2">
        Publish this report? Assigned doctors will no longer be able to change it.
      </Typography>
    </ConfirmCard>
  ) : signatureOpen ? (
    <ConfirmCard
      title="Signature required"
      actions={
        <Button type="button" onClick={() => setSignatureOpen(false)}>
          Close
        </Button>
      }
    >
      <Typography variant="body2">
        A doctor signature is required on your diagnostic profile before you can sign reports.
        Ask an administrator to upload it in Diagnostic Doctor Management.
      </Typography>
    </ConfirmCard>
  ) : null;

  return (
    <>
      <DiagnosticReportPdfModal
        title={copy.pageTitle}
        details={pdfDetails}
        pdfUrl={pdfUrl}
        pdfLoading={loading || pdfLoading}
        headerExtra={
          canViewIpdCondition || canViewEmergencyCondition ? (
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={() => setConditionOpen(true)}
            >
              View patient condition
            </Button>
          ) : canViewPatientTimeline ? (
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={() => setTimelineOpen(true)}
            >
              View patient condition
            </Button>
          ) : null
        }
        footerExtra={footerExtra}
        overlay={overlay}
        onClose={onBack}
        onDownload={() => {
          if (!pdfUrl) return;
          const link = document.createElement("a");
          link.href = pdfUrl;
          link.download = `${copy.domain}_${patient.name || "report"}_${
            report.order_no || ""
          }.pdf`.replace(/\s+/g, "_");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }}
      />
      {canViewIpdCondition ? (
        <IpdPatientConditionDialog
          open={conditionOpen}
          onClose={() => setConditionOpen(false)}
          admissionId={admissionId}
          patientName={reviewPatient?.name || patient?.name || ""}
        />
      ) : null}
      {canViewEmergencyCondition ? (
        <EmergencyPatientConditionDialog
          open={conditionOpen}
          onClose={() => setConditionOpen(false)}
          visitId={emergencyVisitId}
          patientName={reviewPatient?.name || patient?.name || ""}
        />
      ) : null}
      {canViewPatientTimeline ? (
        <PatientTimelineDialog
          open={timelineOpen}
          onClose={() => setTimelineOpen(false)}
          patientId={timelinePatientId}
          patientName={reviewPatient?.name || patient?.name || ""}
        />
      ) : null}
    </>
  );
}

export default function DiagnosticDoctorQueue() {
  const copy = useMemo(
    () => getDiagnosticDoctorCopy(getDiagnosticDoctorRoleType()),
    []
  );
  const [statusFilter, setStatusFilter] = useState("assigned");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  // Same-sample rows captured when the row is opened — the queue reloads while
  // the review is open and signed rows drop out of it.
  const [selectedSiblings, setSelectedSiblings] = useState([]);
  const [forbidden, setForbidden] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const closeDetail = useCallback(() => setSelected(null), []);
  const statusFilterRef = useRef(statusFilter);
  statusFilterRef.current = statusFilter;

  const roleType = getDiagnosticDoctorRoleType();
  const allowed = roleType === "pathologist" || roleType === "radiologist";

  const loadQueue = useCallback(async ({ silent } = {}) => {
    const requested = statusFilterRef.current;
    if (!silent) setLoading(true);
    try {
      const statusParam = requested === "all" ? undefined : requested;
      // Silent polls must skip the App.js full-screen logo loader; otherwise the
      // 60s refresh flashes "Loading..." over an already-rendered queue.
      const res = await getMyDiagnosticAssignments(statusParam, {
        skipGlobalLoader: !!silent,
      });
      if (statusFilterRef.current !== requested) return;
      const list = Array.isArray(res.data) ? res.data : [];
      setRows(list);
      if (requested === "assigned") setPendingCount(list.length);
      setForbidden(false);
    } catch (err) {
      if (statusFilterRef.current !== requested) return;
      const kind = classifyDiagnosticError(err);
      const msg = err?.message || "Could not load queue";
      toast.error(msg);
      if (kind === "logout") {
        invalidAccessLogout({ skipLogoutApi: true });
        return;
      }
      if (kind === "forbidden") setForbidden(true);
      setRows([]);
    } finally {
      if (statusFilterRef.current === requested && !silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) loadQueue();
  }, [loadQueue, allowed, statusFilter]);

  useEffect(() => {
    if (!allowed || selected || statusFilter !== "assigned") return undefined;
    const id = setInterval(() => loadQueue({ silent: true }), 60000);
    return () => clearInterval(id);
  }, [allowed, selected, statusFilter, loadQueue]);

  // Tests on one sample read as one line item: the first row stands for the
  // group and the rest ride along as its siblings.
  const groupedRows = useMemo(() => {
    if (roleType !== "pathologist") {
      return rows.map((row) => ({ row, siblings: [] }));
    }
    const used = new Set();
    const groups = [];
    rows.forEach((row) => {
      if (used.has(row)) return;
      used.add(row);
      const siblings = rows.filter(
        (r) => !used.has(r) && isSameLabSample(row, r) && rowPatientLabTestId(r),
      );
      siblings.forEach((r) => used.add(r));
      // The order the lab submitted first leads the group — the same order
      // the lab report itself opens on.
      const ordered = [row, ...siblings].sort(
        (a, b) =>
          (Date.parse(a.assigned_at) || 0) - (Date.parse(b.assigned_at) || 0),
      );
      groups.push({ row: ordered[0], siblings: ordered.slice(1) });
    });
    return groups;
  }, [rows, roleType]);

  if (!allowed) {
    return (
      <div style={{ padding: "28px 32px" }}>
        <PageHeader title="Diagnostic report review" />
        <Typography color="text.secondary">
          You do not have access to this screen.
        </Typography>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div style={{ padding: "28px 32px" }}>
        <PageHeader title={copy.pageTitle} />
        <Typography color="text.secondary">
          You do not have access to this endpoint.
        </Typography>
      </div>
    );
  }

  return (
    <div style={{ padding: "28px 32px" }}>
      <PageHeader
        title={copy.pageTitle}
        subtitle={tabSubtitle(copy, statusFilter)}
        actions={
          <StatusChip
            meta={{
              label: copy.groupBadge,
              color: C.primary,
              bg: "#e9f5fe",
            }}
          />
        }
      />
      <QueueTabs
        value={statusFilter}
        onChange={setStatusFilter}
        pendingCount={pendingCount}
      />
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">
          {emptyCopy(copy, statusFilter)}
        </Typography>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              minWidth: 980,
              borderCollapse: "collapse",
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ background: C.accent, textAlign: "left" }}>
                {[
                  "Patient",
                  "UHID",
                  "Order",
                  "Test",
                  "Lab status",
                  "My status",
                  "Order publish",
                  "Assigned at",
                  "Priority",
                ].map((h) => (
                  <th key={h} style={{ padding: "10px 12px" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groupedRows.map(({ row, siblings }) => {
                const report = row.report || {};
                const patient = report.patient || {};
                const joinField = (pick) =>
                  [row, ...siblings]
                    .map((r) => pick(r.report || {}))
                    .filter(Boolean)
                    .join(", ") || "—";
                return (
                  <tr
                    key={row.id || row.patient_lab_test_id}
                    onClick={() => {
                      rememberQueueDoctorIdentity(row);
                      setSelectedSiblings(siblings);
                      setSelected(row);
                    }}
                    style={{
                      borderBottom: `1px solid ${C.border}`,
                      cursor: "pointer",
                    }}
                  >
                    <td style={{ padding: "10px 12px" }}>
                      {patient.name || "—"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {patient.uhid || "—"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {report.order_no || "—"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {joinField((r) => r.test)}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {report.status || "—"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <StatusChip
                        meta={ASSIGNMENT_STATUS_META[row.status]}
                      />
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <StatusChip
                        meta={
                          PUBLISH_STATUS_META[report.report_publish_status] ||
                          PUBLISH_STATUS_META.pending
                        }
                      />
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {displayWhen(row.assigned_at)}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {report.priority || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {selected ? (
        <ReviewReportOverlay
          item={selected}
          siblings={selectedSiblings}
          copy={copy}
          onBack={closeDetail}
          onUpdated={() => loadQueue({ silent: true })}
        />
      ) : null}
    </div>
  );
}
